import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';
import { mergeMessagesPages } from 'store/app/entities/messagesListMerge';

const MESSAGES_LOADED_META = 'MESSAGES_LOADED_META';

/** Must stay aligned with `READ_MESSAGES_FULFILLED` in `messages/action.js` (avoid importing action: circular dep with `messagesApiUtil`). */
const READ_MESSAGES_FULFILLED = 'READ_MESSAGES_FULFILLED';

const MessagesApi = createApi({
  reducerPath: 'messagesApi',
  baseQuery,
  tagTypes: ['Messages'],
  endpoints: build => ({
    listMessages: build.query({
      query: ({ channelId, beforeId, fromId, aroundId }) => {
        const params = new URLSearchParams(
          Object.entries({ beforeId, fromId, aroundId }).filter(([, v]) => v != null),
        );
        return { url: `v3/message/${channelId}?${params}` };
      },
      transformResponse: response => response.data,
      /** Cache key = channel id; `updateQueryData('listMessages', { channelId }, …)` must pass the object, not a bare string. */
      serializeQueryArgs: ({ queryArgs }) => queryArgs.channelId,
      merge: (cache, incoming, { arg }) => mergeMessagesPages(cache, incoming, arg),
      providesTags: (result, error, { channelId }) => [{ type: 'Messages', id: channelId }],
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        if (currentArg.channelId !== previousArg.channelId) return true;
        const prevAround = previousArg.aroundId || null;
        const currAround = currentArg.aroundId || null;
        // Refetch when aroundId is newly set or changes — not when cleared after anchor (OMM-632).
        if (currAround && currAround !== prevAround) return true;
        return false;
      },
      async onQueryStarted({ channelId, aroundId }, { dispatch, queryFulfilled }) {
        if (aroundId) {
          dispatch(
            MessagesApi.util.updateQueryData('listMessages', { channelId }, draft => {
              draft.messages = [];
              draft.hasOlder = true;
              draft.hasNewer = true;
              draft.awaitingAroundId = true;
            }),
          );
        }
        try {
          const { data } = await queryFulfilled;
          const { unreadCount, lastReadMessage, firstReadMessage, lastSeen } = data;
          dispatch({
            type: MESSAGES_LOADED_META,
            payload: { channelId, unreadCount, lastReadMessage, firstReadMessage, lastSeen },
          });
        } catch {
          if (aroundId) {
            dispatch(
              MessagesApi.util.updateQueryData('listMessages', { channelId }, draft => {
                if (draft) {
                  draft.awaitingAroundId = false;
                }
              }),
            );
          }
        }
      },
    }),
    readMessages: build.mutation({
      // Superadmins browse for support — never hit the read endpoint (OMM-699).
      async queryFn({ channelId, firstReadMessage }, { getState }, _extra, fetchWithBQ) {
        if (getState().auth?.isSuperAdmin) {
          return { data: { read: false, superAdmin: true } };
        }
        const result = await fetchWithBQ({
          url: `v1/message/${channelId}/read`,
          method: 'PATCH',
          body: { firstReadMessage },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data?.data ?? result.data };
      },
      async onQueryStarted({ channelId }, { dispatch, queryFulfilled, getState }) {
        if (getState().auth?.isSuperAdmin) {
          return;
        }
        try {
          await queryFulfilled;
          // eslint-disable-next-line camelcase -- matches Redux auth state field name
          const authUser_id = getState().auth?.user_id;
          dispatch({
            type: READ_MESSAGES_FULFILLED,
            // eslint-disable-next-line camelcase -- matches Redux action meta field names
            meta: { channel_id: channelId, authUser_id },
          });
          dispatch(
            MessagesApi.util.updateQueryData('listMessages', { channelId }, draft => {
              if (draft && typeof draft.unreadCount === 'number') {
                draft.unreadCount = 0;
              }
            }),
          );
        } catch {
          /* PATCH failed — do not clear unread in Redux */
        }
      },
    }),
  }),
});

export const {
  useListMessagesQuery,
  useLazyListMessagesQuery,
  useReadMessagesMutation,
  util: messagesApiUtil,
} = MessagesApi;

export { MESSAGES_LOADED_META };
export const { middleware: messagesApiMiddleware, reducer: messagesApiReducer } = MessagesApi;
export default MessagesApi;
